import React, { useRef, useState, useEffect } from 'react';
import { Button, Typography, Alert, Image, Card, Space, Row, Col, Divider, message } from 'antd';
import { CameraOutlined, DownloadOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const CameraCapture: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    async function startCamera() {
      setIsLoading(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'environment'
          } 
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError('Ошибка доступа к камере: ' + message);
      } finally {
        setIsLoading(false);
      }
    }

    startCamera();

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);

  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/png');
    setPhoto(dataUrl);

    // Автоматически отправляем фото на сервер
    await uploadPhoto(dataUrl);
  };

  const uploadPhoto = async (photoDataUrl: string) => {
    setIsUploading(true);
    try {
      // Конвертируем base64 в Blob
      const response = await fetch(photoDataUrl);
      const blob = await response.blob();
      
      // Создаем FormData
      const formData = new FormData();
      formData.append('photo', blob, 'photo.png');

      // Отправляем на сервер
      const uploadResponse = await fetch('https://eggs.aeve.ru/api/photos', {
        method: 'POST',
        body: formData,
      });

      if (uploadResponse.ok) {
        const result = await uploadResponse.json();
        message.success('Фото успешно сохранено на сервере!');
        console.log('Photo saved with ID:', result.id);
      } else {
        throw new Error('Ошибка загрузки на сервер');
      }
    } catch (err) {
      console.error('Upload error:', err);
      message.error('Ошибка при сохранении фото на сервер');
    } finally {
      setIsUploading(false);
    }
  };

  const retakePhoto = () => {
    setPhoto(null);
  };

  const downloadPhoto = () => {
    if (!photo) return;
    
    const link = document.createElement('a');
    link.href = photo;
    link.download = `photo_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="camera-container">
      {error && (
        <Alert 
          message={error} 
          type="error" 
          showIcon 
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" type="primary" onClick={() => window.location.reload()}>
              Перезагрузить
            </Button>
          }
        />
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <CameraOutlined />
                <span>Камера</span>
              </Space>
            }
            className="camera-card"
          >
            <div className="video-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="camera-video"
              />
              {isLoading && (
                <div className="video-loading">
                  <Text>Загрузка камеры...</Text>
                </div>
              )}
            </div>
            
            <Divider />
            
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button 
                type="primary" 
                size="large"
                icon={<CameraOutlined />}
                onClick={takePhoto}
                loading={isLoading || isUploading}
                block
              >
                {isUploading ? 'Сохранение...' : 'Сделать фото'}
              </Button>
              
              {isUploading && (
                <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                  <UploadOutlined /> Загрузка на сервер...
                </Text>
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title="Результат"
            className="result-card"
          >
            {photo ? (
              <div className="photo-result">
                <Image 
                  src={photo} 
                  alt="Сделанное фото" 
                  className="captured-photo"
                  preview={{
                    mask: 'Нажмите для увеличения'
                  }}
                />
                
                <Divider />
                
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    type="primary" 
                    icon={<DownloadOutlined />}
                    onClick={downloadPhoto}
                    block
                  >
                    Скачать фото
                  </Button>
                  
                  <Button 
                    icon={<ReloadOutlined />}
                    onClick={retakePhoto}
                    block
                  >
                    Сделать новое фото
                  </Button>
                </Space>
              </div>
            ) : (
              <div className="no-photo">
                <CameraOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
                <Text type="secondary">Фото еще не сделано</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default CameraCapture;
